import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';

// Premium Roles Index - Organizational Control
function RolesIndex() {
    const [roles, setRoles] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    useEffect(() => {
        loadRoles();
    }, [currentPage, searchTerm]);

    const loadRoles = async () => {
        try {
            setLoading(true);
            const params: any = { 
                page: currentPage,
                q: searchTerm
            };
            const response = await api.get('/settings/roles', { params });
            if (response.success) {
                const rolesData = Array.isArray(response.data) ? response.data : (response.data?.roles || response.data?.data || []);
                setRoles(rolesData);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (err) {
            console.error('Error loading roles:', err);
            setError('Unable to synchronize role hierarchy.');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading && roles.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-black tracking-widest uppercase text-[10px]">Syncing Permissions...</div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-12">
                <div>
                    <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
                        Security <span className="text-indigo-600">Roles</span>
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium italic">Define access levels and functional permissions ({pagination.total_count} roles)</p>
                </div>
                <Link
                    to="/settings/roles/new"
                    className="group relative inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:bg-black hover:scale-105 active:scale-95 shadow-2xl shadow-indigo-100"
                >
                    <span className="relative z-10 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" />
                        </svg>
                        Create New Role
                    </span>
                </Link>
            </div>

            {/* Search */}
            <div className="relative group mb-10 max-w-md">
                <input
                    type="text"
                    placeholder="Search by role name..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full px-6 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium text-slate-600 group-hover:shadow-md"
                />
                <div className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-6 rounded-3xl mb-10 shadow-sm">
                    <p className="text-sm font-black text-rose-800 uppercase tracking-wider">{error}</p>
                </div>
            )}

            {roles.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-100 shadow-2xl p-20 text-center">
                    <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-8 text-indigo-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2">No Access Scopes</h3>
                    <p className="text-slate-400 font-medium italic mb-8 max-w-xs mx-auto">No roles found matching your criteria.</p>
                    <Link to="/settings/roles/new" className="text-indigo-600 font-black uppercase text-xs tracking-widest">+ New Security Level</Link>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                        {roles.map((role) => (
                            <div key={role.id} className="group relative bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-indigo-100 flex flex-col h-full overflow-hidden">
                                <div className="flex items-center justify-between mb-8">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black transition-all group-hover:scale-110 duration-500 ${role.system_role ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'
                                        }`}>
                                        {role.name.charAt(0)}
                                    </div>
                                    {role.system_role && (
                                        <span className="px-3 py-1 bg-slate-900 text-white text-[8px] font-black uppercase tracking-widest rounded-full">Systems Level</span>
                                    )}
                                </div>

                                <div className="flex-1">
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2 group-hover:text-indigo-600 transition-colors">
                                        {role.name}
                                    </h3>
                                    <p className="text-slate-500 text-sm font-medium italic line-clamp-2 mb-6">
                                        {role.description || 'No specialized description provided for this security scope.'}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                                    <div className="flex -space-x-2">
                                        {[...Array(Math.min(role.members_count || 0, 3))].map((_, i) => (
                                            <div key={i} className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-black text-slate-400 ring-1 ring-slate-100">
                                                •
                                            </div>
                                        ))}
                                        {(role.members_count || 0) > 3 && (
                                            <div className="w-8 h-8 rounded-full bg-indigo-50 border-2 border-white flex items-center justify-center text-[8px] font-black text-indigo-600">
                                                +{(role.members_count || 0) - 3}
                                            </div>
                                        )}
                                        {(!role.members_count || role.members_count === 0) && (
                                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No Active Personnel</span>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Link
                                            to={`/settings/roles/${role.id}/edit`}
                                            className="p-3 bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-900 hover:text-white transition-all duration-300"
                                            title="Refine Role"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                            </svg>
                                        </Link>
                                        <Link
                                            to={`/settings/roles/${role.id}/permissions`}
                                            className="p-3 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white transition-all duration-300"
                                            title="Access Matrix"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Pagination
                        currentPage={pagination.current_page}
                        totalPages={pagination.total_pages}
                        totalCount={pagination.total_count}
                        perPage={pagination.per_page}
                        onPageChange={handlePageChange}
                    />
                </>
            )}
        </div>
    );
}

export default RolesIndex;
